import { Checkbox } from '@material-ui/core';
import React, { useEffect, useRef } from 'react';

interface SelectAllCheckboxProps {
  checked: boolean;
  /** Some, but not all. False at both ends — see below. */
  indeterminate: boolean;
  disabled: boolean;
  label: string;
  onChange: () => void;
}

/**
 * The header checkbox, with its mixed state actually announced.
 *
 * MUI v4's `Checkbox` takes `indeterminate` and spends it on presentation: it
 * swaps the glyph for a dash and puts `data-indeterminate` on the input for its
 * own styling. It never sets the input's `indeterminate` DOM property, which is
 * the only thing assistive tech reads — so a header checkbox covering a partly
 * selected page reports itself as plainly unchecked, and a screen reader user
 * pressing it is told they are selecting a page they have already half selected.
 *
 * `aria-checked="mixed"` is not the fix. ARIA in HTML disallows it on
 * `input[type=checkbox]` precisely because the host language has its own tri-state,
 * and that state is the property. So it is set through a ref, which is what a
 * native tri-state checkbox has always needed.
 *
 * Extracted rather than inlined because the ref and the effect are the whole
 * component, and a hook pair sitting in the middle of a table header reads as
 * incidental.
 */
export const SelectAllCheckbox: React.FC<SelectAllCheckboxProps> = ({
  checked,
  indeterminate,
  disabled,
  label,
  onChange,
}) => {
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (input.current) input.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <Checkbox
      checked={checked}
      color="primary"
      disabled={disabled}
      indeterminate={indeterminate}
      inputProps={{ 'aria-label': label }}
      inputRef={input}
      size="small"
      onChange={onChange}
    />
  );
};
